import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { API_URL } from "config";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";

const VALID_EFFECTS = new Set(["allow", "deny"]);

const permissionId = (permission) => String(permission?.id ?? "");

const permissionLabel = (permission) => permission?.name || permission?.key || "Permission";

const overrideStateFromPermissions = (permissions) => permissions.reduce((state, permission) => {
  if (permission?.assignable && VALID_EFFECTS.has(permission?.override)) {
    state[permissionId(permission)] = permission.override;
  }

  return state;
}, {});

const overridesEqual = (left, right) => {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  return leftKeys.length === rightKeys.length
    && leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
};

const effectiveForPermission = (permission, overrides) => {
  if (!permission?.assignable) return Boolean(permission?.effective);

  const override = overrides[permissionId(permission)] || null;
  if (override === "allow") return true;
  if (override === "deny") return false;
  return Boolean(permission?.role_granted);
};

const statusForPermission = (permission, overrides) => {
  const override = permission?.assignable
    ? (overrides[permissionId(permission)] || null)
    : (VALID_EFFECTS.has(permission?.override) ? permission.override : null);

  if (override === "allow") {
    return { label: "User-specific allow", override };
  }

  if (override === "deny") {
    return { label: "User-specific block", override };
  }

  return { label: "", override: null };
};

const UserPermissionOverrides = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [initialOverrides, setInitialOverrides] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const leavePage = useCallback(() => {
    if (location.state?.fromUsersList) {
      navigate(-1);
      return;
    }

    navigate("/users/list", { replace: true });
  }, [location.state, navigate]);

  const request = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("authToken");
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + token,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });
    const data = await readJsonSafely(response);

    if (!response.ok) {
      const requestError = new Error(apiErrorMessage(data, "Request failed (" + response.status + ")"));
      requestError.status = response.status;
      throw requestError;
    }

    return data || {};
  }, []);

  const applySnapshot = useCallback((data, resetExpansion = false) => {
    const rows = Array.isArray(data?.permissions) ? data.permissions : [];
    const nextOverrides = overrideStateFromPermissions(rows);

    setPermissions(rows);
    setOverrides(nextOverrides);
    setInitialOverrides(nextOverrides);
    setCanManage(data?.can_manage === undefined ? true : Boolean(data.can_manage));

    if (resetExpansion) setExpanded([]);
  }, []);

  useEffect(() => {
    if (!userId) return undefined;

    const controller = new AbortController();
    setPermissions([]);
    setOverrides({});
    setInitialOverrides({});
    setExpanded([]);
    setLoading(true);
    setCanManage(null);
    setError("");
    setSuccess("");
    setConfirmation(null);

    request(
      API_URL + "/access/users/" + encodeURIComponent(String(userId)) + "/permission-overrides",
      { signal: controller.signal }
    )
      .then((data) => applySnapshot(data, true))
      .catch((requestError) => {
        if (requestError?.name === "AbortError") return;

        if (requestError?.status === 403) {
          setCanManage(false);
          setError("You cannot manage permissions for this user. The user may be outside your role hierarchy or assigned scope.");
          return;
        }

        setError(requestError?.message || "Failed to load user-specific permissions.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [applySnapshot, request, userId]);

  const permissionGraph = useMemo(() => {
    const byId = new Map();
    const childrenByParent = new Map();

    permissions.forEach((permission) => {
      const id = permissionId(permission);
      if (id) byId.set(id, permission);
    });

    permissions.forEach((permission) => {
      const id = permissionId(permission);
      const parentId = String(permission?.parent_id ?? "");
      if (!id || !parentId || !byId.has(parentId)) return;

      const children = childrenByParent.get(parentId) || [];
      children.push(permission);
      childrenByParent.set(parentId, children);
    });

    const subtreeById = new Map();
    const buildSubtree = (id, visiting = new Set()) => {
      if (subtreeById.has(id)) return subtreeById.get(id);
      if (visiting.has(id)) return [];

      const permission = byId.get(id);
      if (!permission) return [];

      const nextVisiting = new Set(visiting);
      nextVisiting.add(id);
      const subtree = [permission];
      (childrenByParent.get(id) || []).forEach((child) => {
        subtree.push(...buildSubtree(permissionId(child), nextVisiting));
      });
      subtreeById.set(id, subtree);
      return subtree;
    };

    byId.forEach((permission, id) => buildSubtree(id));

    const roots = permissions.filter((permission) => {
      const parentId = String(permission?.parent_id ?? "");
      return !parentId || !byId.has(parentId);
    });
    const coveredIds = new Set();
    const markCovered = (permission, ancestorIds = new Set()) => {
      const id = permissionId(permission);
      if (!id || coveredIds.has(id) || ancestorIds.has(id)) return;

      coveredIds.add(id);
      const nextAncestorIds = new Set(ancestorIds);
      nextAncestorIds.add(id);
      (childrenByParent.get(id) || []).forEach((child) => markCovered(child, nextAncestorIds));
    };

    roots.forEach((permission) => markCovered(permission));
    permissions.forEach((permission) => {
      const id = permissionId(permission);
      if (!id || coveredIds.has(id)) return;
      roots.push(permission);
      markCovered(permission);
    });

    return { byId, childrenByParent, roots, subtreeById };
  }, [permissions]);

  const effectiveById = useMemo(() => {
    const values = new Map();
    permissions.forEach((permission) => {
      values.set(permissionId(permission), effectiveForPermission(permission, overrides));
    });
    return values;
  }, [overrides, permissions]);

  const aggregateById = useMemo(() => {
    const values = new Map();

    permissionGraph.subtreeById.forEach((subtree, id) => {
      const editableRows = subtree.filter((permission) => Boolean(permission?.assignable));
      values.set(id, {
        rows: editableRows,
        allowed: editableRows.filter((permission) => effectiveById.get(permissionId(permission))).length,
        total: editableRows.length,
      });
    });

    return values;
  }, [effectiveById, permissionGraph]);

  const dirty = useMemo(
    () => !overridesEqual(overrides, initialOverrides),
    [initialOverrides, overrides]
  );

  const setOverride = useCallback((id, effect) => {
    setSuccess("");
    setError("");
    setOverrides((current) => {
      const next = { ...current };

      if (effect === null) {
        delete next[String(id)];
      } else {
        next[String(id)] = effect;
      }

      return next;
    });
  }, []);

  const setRowsEffective = (rows, shouldAllow) => {
    setSuccess("");
    setError("");
    setOverrides((current) => {
      const next = { ...current };

      rows.forEach((permission) => {
        if (!permission?.assignable) return;
        if (effectiveForPermission(permission, current) === shouldAllow) return;

        const id = permissionId(permission);
        const nextEffect = shouldAllow
          ? (permission.role_granted ? null : "allow")
          : (permission.role_granted ? "deny" : null);

        if (nextEffect === null) {
          delete next[id];
        } else {
          next[id] = nextEffect;
        }
      });

      return next;
    });
  };

  const togglePermission = (permission) => {
    const id = permissionId(permission);
    const isParent = (permissionGraph.childrenByParent.get(id) || []).length > 0;
    const aggregate = aggregateById.get(id) || { rows: [], allowed: 0, total: 0 };
    const rows = isParent ? aggregate.rows : (permission?.assignable ? [permission] : []);
    if (rows.length === 0 || saving || confirmation) return;

    const shouldAllow = !rows.every((row) => effectiveForPermission(row, overrides));
    const changingCount = rows.filter(
      (row) => effectiveForPermission(row, overrides) !== shouldAllow
    ).length;

    if (isParent && rows.length > 1) {
      setConfirmation({
        type: "bulk",
        permissionIds: rows.map(permissionId),
        shouldAllow,
        label: permissionLabel(permission),
        count: changingCount,
        total: rows.length,
      });
      return;
    }

    setRowsEffective(rows, shouldAllow);
  };

  const toggleExpanded = (id) => {
    const normalizedId = String(id);
    setExpanded((current) => (
      current.includes(normalizedId)
        ? current.filter((value) => value !== normalizedId)
        : [...current, normalizedId]
    ));
  };

  const save = async () => {
    if (!dirty || saving || canManage === false) return;

    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      overrides: permissions
        .filter((permission) => (
          permission.assignable && VALID_EFFECTS.has(overrides[permissionId(permission)])
        ))
        .map((permission) => ({
          permission_id: permission.id,
          effect: overrides[permissionId(permission)],
        })),
    };

    try {
      const data = await request(
        API_URL + "/access/users/" + encodeURIComponent(String(userId)) + "/permission-overrides",
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );
      applySnapshot(data);
      window.dispatchEvent(new CustomEvent("access-permissions-changed", {
        detail: { userId: String(userId) },
      }));
      setSuccess(apiSuccessMessage(data, "User-specific permissions updated."));
    } catch (requestError) {
      if (requestError?.status === 403) {
        setCanManage(false);
        setError("You no longer have permission to manage this user.");
      } else {
        setError(requestError?.message || "Failed to update user-specific permissions.");
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelConfirmation = () => {
    if (!saving) setConfirmation(null);
  };

  const confirmPendingAction = () => {
    const pending = confirmation;
    if (!pending || saving) return;

    if (pending.type === "discard") {
      setConfirmation(null);
      leavePage();
      return;
    }

    const permissionIds = new Set(pending.permissionIds || []);
    const rows = permissions.filter((permission) => (
      permission.assignable && permissionIds.has(permissionId(permission))
    ));
    setRowsEffective(rows, Boolean(pending.shouldAllow));
    setConfirmation(null);
  };

  const requestLeave = () => {
    if (saving || confirmation) return;
    if (dirty) {
      setConfirmation({ type: "discard" });
      return;
    }
    leavePage();
  };

  const renderPermission = (permission, level = 0, ancestorIds = new Set()) => {
    const id = permissionId(permission);
    const fullChildren = permissionGraph.childrenByParent.get(id) || [];
    const nextAncestorIds = new Set(ancestorIds);
    nextAncestorIds.add(id);
    const children = fullChildren.filter((child) => !nextAncestorIds.has(permissionId(child)));
    const isParent = fullChildren.length > 0;
    const hasChildren = children.length > 0;
    const isOpen = expanded.includes(id);
    const aggregate = aggregateById.get(id) || { rows: [], allowed: 0, total: 0 };
    const ownEffective = Boolean(effectiveById.get(id));
    const allowedDescendants = aggregate.rows
      .filter((row) => permissionId(row) !== id)
      .filter((row) => effectiveById.get(permissionId(row)))
      .length;
    const indeterminate = isParent && !ownEffective && allowedDescendants > 0;
    const status = statusForPermission(permission, overrides);
    const canToggle = isParent
      ? aggregate.rows.length > 0
      : Boolean(permission?.assignable);

    return (
      <React.Fragment key={id}>
        <SoftBox
          pl={level * 3}
          py={0.75}
          display="flex"
          alignItems="center"
          sx={{ userSelect: "none" }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => toggleExpanded(id)}
              aria-label={(isOpen ? "Collapse " : "Expand ") + permissionLabel(permission)}
            >
              <ChevronRightIcon
                fontSize="small"
                sx={{
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          ) : (
            <Box width={40} />
          )}

          <input
            type="checkbox"
            checked={ownEffective}
            disabled={!canToggle || saving}
            onChange={() => togglePermission(permission)}
            ref={(input) => {
              if (input) input.indeterminate = indeterminate;
            }}
            aria-label={permissionLabel(permission)}
            style={{
              width: 16,
              height: 16,
              marginRight: 8,
              flexShrink: 0,
              cursor: canToggle && !saving ? "pointer" : "not-allowed",
              accentColor: status.override === "allow" ? "#82d616" : "#17c1e8",
            }}
          />

          <SoftTypography
            variant="body2"
            fontWeight={level === 0 ? 600 : 400}
            color="dark"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {permissionLabel(permission)}
          </SoftTypography>

          <SoftBox display="flex" alignItems="center" gap={0.75} ml={1}>
            {status.override && (
              <SoftTypography
                variant="caption"
                color={status.override === "allow" ? "success" : "error"}
                fontWeight="bold"
                sx={{ whiteSpace: "nowrap" }}
              >
                {status.label}
              </SoftTypography>
            )}
            {!permission.assignable && (
              <SoftTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
                {isParent && aggregate.rows.length > 0 ? "Editable children only" : "View only"}
              </SoftTypography>
            )}
            {permission.assignable && status.override && (
              <Tooltip title="Restore this permission to the role setting">
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    disabled={saving}
                    onClick={() => setOverride(id, null)}
                    aria-label={"Restore role setting for " + permissionLabel(permission)}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </SoftBox>
        </SoftBox>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {children.map((child) => renderPermission(child, level + 1, nextAncestorIds))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const isDiscardConfirmation = confirmation?.type === "discard";
  const confirmationTitle = isDiscardConfirmation
    ? "Discard unsaved changes?"
    : confirmation?.shouldAllow
      ? "Allow these permissions?"
      : "Remove these permissions?";
  const confirmationMessage = isDiscardConfirmation
    ? "Your unsaved user-specific permission changes will be discarded."
    : (confirmation?.label || "This permission group")
      + " will change "
      + (confirmation?.count || 0)
      + " of "
      + (confirmation?.total || 0)
      + " editable permissions for this user. Nothing is saved until you click Update User Specific Permissions.";
  const confirmationColor = isDiscardConfirmation || !confirmation?.shouldAllow
    ? "error"
    : "info";
  const confirmationButtonLabel = isDiscardConfirmation
    ? "Discard changes"
    : confirmation?.shouldAllow
      ? "Allow permissions"
      : "Remove permissions";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox p={3}>
        <SoftBox mb={2}>
          <SoftButton
            type="button"
            variant="outlined"
            color="dark"
            startIcon={<ArrowBackIcon />}
            onClick={requestLeave}
            disabled={saving || Boolean(confirmation)}
          >
            Back
          </SoftButton>
        </SoftBox>

        <Card sx={{ p: 2, mb: 3 }}>
          <SoftTypography variant="h6" mb={1}>
            Permissions
          </SoftTypography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <SoftBox py={8} display="flex" justifyContent="center">
              <CircularProgress />
            </SoftBox>
          ) : canManage === false ? null : permissions.length === 0 ? (
            <SoftBox py={4}>
              <SoftTypography variant="body2" color="text">
                No permissions are available for this user.
              </SoftTypography>
            </SoftBox>
          ) : (
            permissionGraph.roots.map((permission) => renderPermission(permission))
          )}
        </Card>

        <SoftBox display="flex" gap={2} flexWrap="wrap">
          {canManage !== false && (
            <SoftButton
              type="button"
              variant="gradient"
              color="info"
              onClick={save}
              disabled={loading || saving || !dirty || Boolean(confirmation)}
            >
              {saving ? "Updating..." : "Update User Specific Permissions"}
            </SoftButton>
          )}
          <SoftButton
            type="button"
            variant="outlined"
            color="dark"
            onClick={requestLeave}
            disabled={saving || Boolean(confirmation)}
          >
            Cancel
          </SoftButton>
        </SoftBox>
      </SoftBox>

      <Dialog
        open={Boolean(confirmation)}
        onClose={cancelConfirmation}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={saving}
        aria-labelledby="user-permission-confirmation-title"
      >
        <DialogTitle id="user-permission-confirmation-title">
          {confirmationTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmationMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <SoftButton
            type="button"
            color={confirmationColor}
            variant="contained"
            onClick={confirmPendingAction}
            disabled={saving}
          >
            {confirmationButtonLabel}
          </SoftButton>
          <SoftButton
            type="button"
            color="secondary"
            variant="outlined"
            onClick={cancelConfirmation}
            disabled={saving}
          >
            Cancel
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default UserPermissionOverrides;
